import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { extractApiErrorMessage } from "../../../utils/apiError";

interface ManagerApplication {
  [key: string]: any;
}

interface ManagerPortalState {
  applications: ManagerApplication[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  savingPaidTimeOff: boolean;
  savePaidTimeOffError: string | null;
  timeOffDetails?: {
    paidTimeOffHours?: number;
    paidTimeOffDate?: string;
  } | null;
  loadingTimeOffDetails?: boolean;
  timeOffDetailsError?: string | null;
}

const initialState: ManagerPortalState = {
  applications: [],
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  totalPages: 0,
  savingPaidTimeOff: false,
  savePaidTimeOffError: null,
  timeOffDetails: null,
  loadingTimeOffDetails: false,
  timeOffDetailsError: null,
};

export const fetchManagerApplicationsList = createAsyncThunk(
  "managerPortal/fetchManagerApplicationsList",
  async (
    { page, perPage }: { page: number; perPage: number },
    { rejectWithValue, signal }
  ) => {
    try {
      const options: any = {
        signal,
        params: {
          page,
          per_page: perPage,
        },
      };

      const response = await api.get(
        endpoints.citizenship.managerPortal.managerApplicationsList,
        options
      );

      if (response.data?.success === false) {
        return rejectWithValue(
          extractApiErrorMessage({ response }, "Failed to fetch manager applications"),
        );
      }

      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.response?.statusText ||
        error?.message ||
        "Failed to fetch manager applications";

      return rejectWithValue(errorMessage);
    }
  }
);

export const savePaidOffTime = createAsyncThunk(
  "managerPortal/savePaidOffTime",
  async (
    {
      applicationId,
      paidTimeOffHours,
      paidTimeOffDate,
      empNuid,
    }: {
      applicationId: string;
      paidTimeOffHours: string | number;
      paidTimeOffDate: string;
      empNuid?: string | null;
    },
    { rejectWithValue, signal }
  ) => {
    try {
      if (!applicationId) throw new Error("applicationId is required");

      const url = endpoints.citizenship.managerPortal.managerPaidTimeOff(applicationId);
      const options: any = {
        signal,
        headers: {},
      };

      if (empNuid) {
        options.headers["X-emp-nuid"] = empNuid;
      }

      const response = await api.patch(
        url,
        {
          paidTimeOffHours: Number(paidTimeOffHours),
          paidTimeOffDate,
        },
        options
      );

      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }

     

      return rejectWithValue(extractApiErrorMessage(error, "Failed to save paid time off details"));
    }
  }
);

export const fetchTimeOffDetails = createAsyncThunk(
  "managerPortal/fetchTimeOffDetails",
  async (
    { applicationId, empNuid }: { applicationId: string; empNuid?: string | null },
    { rejectWithValue, signal }
  ) => {
    try {
      if (!applicationId) throw new Error("applicationId is required");
      const url = endpoints.citizenship.managerPortal.managerPaidTimeOff(applicationId);
      const options: any = { signal, headers: {} };
      if (empNuid) {
        options.headers["X-emp-nuid"] = empNuid;
      }
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.response?.statusText ||
        error?.message ||
        "Failed to fetch time off details";
      return rejectWithValue(errorMessage);
    }
  }
);

const managerPortalSlice = createSlice({
  name: "managerPortal",
  initialState,
  reducers: {
    resetManagerPortalState: (state) => {
      state.applications = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchManagerApplicationsList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchManagerApplicationsList.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      // Handle API response with meta object and items array (prioritize this format)
      if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
        console.log("Processing meta + items format. Items:", payload.items);
        state.applications = payload.items;
        state.totalItems = payload.meta.total || 0;
        state.totalPages = payload.meta.totalPages || 1;
        state.currentPage = payload.meta.pageNumber || 1;
        state.perPage = payload.meta.pageSize || 10;
      } else {
        state.applications = payload ? [payload] : [];
      }
    });
    builder.addCase(fetchManagerApplicationsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // savePaidOffTime cases
    builder.addCase(savePaidOffTime.pending, (state) => {
      state.savingPaidTimeOff = true;
      state.savePaidTimeOffError = null;
    });
    builder.addCase(savePaidOffTime.fulfilled, (state, action) => {
      state.savingPaidTimeOff = false;
      const payload: any = action.payload || {};
      // Persist saved values locally so UI can reflect them without an extra fetch
      state.timeOffDetails = {
        paidTimeOffHours:
          payload.timeOffNumOfHrs ?? Number(action.meta.arg?.paidTimeOffHours),
        paidTimeOffDate:
          payload.timeOffDate ?? action.meta.arg?.paidTimeOffDate,
      };
    });
    builder.addCase(savePaidOffTime.rejected, (state, action) => {
      state.savingPaidTimeOff = false;
      state.savePaidTimeOffError = action.payload as string;
    });
    builder.addCase(fetchTimeOffDetails.pending, (state) => {
      state.loadingTimeOffDetails = true;
      state.timeOffDetailsError = null;
    });
    builder.addCase(fetchTimeOffDetails.fulfilled, (state, action) => {
      state.loadingTimeOffDetails = false;
      const payload: any = action.payload || {};
      state.timeOffDetails = {
        paidTimeOffHours: payload.timeOffNumOfHrs ?? undefined,
        paidTimeOffDate:  payload.timeOffDate ?? undefined,
      };
    });
    builder.addCase(fetchTimeOffDetails.rejected, (state, action) => {
      state.loadingTimeOffDetails = false;
      state.timeOffDetailsError = action.payload as string;
    });
  },
});

export const { resetManagerPortalState, setCurrentPage } =
  managerPortalSlice.actions;
export default managerPortalSlice.reducer;
